import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import {
  useCompanyOverviewControllerGetCompanyOverviewQuery,
  useUsageLimitControllerUsageLimitsQuery,
} from '@shared/services/apiService';
import { CompanyBillingStatus } from '@features/Admin/Billing/ui/CompanyBillingStatus';
import { FilterCard } from '@shared/components/filterCard/FilterCard';
import { useDesignVariant } from '@shared/designVariants/DesignVariantProvider';
import { V5Dashboard } from './V5Dashboard';

export const CompanyDashboard = ({
  concurrency = false,
  onDirtyChange = ignoreDirty,
  requestNavigation = navigateDirectly,
}: {
  concurrency?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  requestNavigation?: (action: () => void) => void;
}) => {
  const { variant } = useDesignVariant();
  if (variant === 'v5' && !concurrency)
    return (
      <V5Dashboard
        overview={<NativeCompanyDashboard />}
        onDirtyChange={onDirtyChange}
        requestNavigation={requestNavigation}
      />
    );
  return <NativeCompanyDashboard concurrency={concurrency} />;
};
const ignoreDirty = () => {};
const navigateDirectly = (action: () => void) => action();
const NativeCompanyDashboard = ({ concurrency = false }: { concurrency?: boolean }) => {
  const overview = useCompanyOverviewControllerGetCompanyOverviewQuery();
  const usage = useUsageLimitControllerUsageLimitsQuery();
  if (overview.isLoading || usage.isLoading)
    return <CircularProgress aria-label="Loading company overview" />;
  if (overview.isError || usage.isError)
    return (
      <Alert
        severity="error"
        action={
          <Button
            onClick={() => {
              void overview.refetch();
              void usage.refetch();
            }}>
            Retry
          </Button>
        }>
        Unable to load current company data.
      </Alert>
    );
  const metrics = concurrency
    ? [
        ['Active calls', usage.currentData?.concurrent?.activeCalls],
        ['Concurrent call limit', usage.currentData?.concurrent?.limit],
      ]
    : [
        ['Campaigns', overview.currentData?.totalCampaigns],
        ['Running campaigns', overview.currentData?.runningCampaigns],
        ['Inactive campaigns', overview.currentData?.inactiveCampaigns],
        ['Ongoing calls', overview.currentData?.ongoingCalls],
      ];
  return (
    <Stack gap={3}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))',
          },
          gap: 2,
        }}>
        {metrics.map(([label, value]) => (
          <Paper key={label} variant="outlined" sx={{ p: 2 }}>
            <Typography color="text.secondary">{label}</Typography>
            <Typography variant="h4">{value == null ? 'Unavailable' : value}</Typography>
          </Paper>
        ))}
      </Box>
      <CompanyBillingStatus />
      <FilterCard />
      <Typography variant="body2" color="text.secondary">
        Company capacity and usage are controlled by the current server entitlements. Select a
        campaign to manage its reserved capacity.
      </Typography>
    </Stack>
  );
};
